import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Translate, withLocalize } from 'react-localize-redux';
import { ServiceType, RequestStatus } from '../../Request/types';
import { getServicesInvestigationService } from '../../../../services/agenda';
import { useDepartments } from '../../../../hooks';
import {
  EMPTY_SURGERY_ANALYTICS_FILTERS,
  SurgeryAnalyticsFiltersQuery,
} from '../types';

const SURGERY_FILTER_STATUSES: RequestStatus[] = [
  RequestStatus.PENDING_PAYMENT,
  RequestStatus.PAYMENT_COMPLETED,
  RequestStatus.PENDING_ANESTHESIOLOGIST,
  RequestStatus.ANESTHESIOLOGIST_ACCEPTED,
  RequestStatus.ANESTHESIOLOGIST_REJECTED,
  RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS,
  RequestStatus.READY_FOR_SURGERY,
  RequestStatus.CHECKED_IN,
  RequestStatus.COMPLETED,
  RequestStatus.CANCELED,
];

const STATUS_I18N_KEY: Partial<Record<RequestStatus, string>> = {
  [RequestStatus.PENDING_PAYMENT]: 'PENDING_PAYMENT',
  [RequestStatus.CANCELED]: 'CANCELED',
  [RequestStatus.COMPLETED]: 'COMPLETED',
  [RequestStatus.PAYMENT_COMPLETED]: 'PAYMENT_COMPLETED',
  [RequestStatus.READY_FOR_SURGERY]: 'READY_FOR_SURGERY',
  [RequestStatus.CHECKED_IN]: 'CHECKED_IN',
  [RequestStatus.PENDING_ANESTHESIOLOGIST]: 'PENDING_ANESTHESIOLOGIST',
  [RequestStatus.ANESTHESIOLOGIST_REJECTED]: 'ANESTHESIOLOGIST_REJECTED',
  [RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS]: 'PENDING_PURCHASE_SURGERY_PROTHESIS',
  [RequestStatus.ANESTHESIOLOGIST_ACCEPTED]: 'ANESTHESIOLOGIST_ACCEPTED',
};

export interface SurgeryAnalyticsFiltersProps {
  uuidInvestigation: string;
  value: SurgeryAnalyticsFiltersQuery;
  onChange: (next: SurgeryAnalyticsFiltersQuery) => void;
  onApply: () => void;
}

const SurgeryAnalyticsFiltersComponent: React.FC<SurgeryAnalyticsFiltersProps & { translate: (id: string) => string }> = ({
  uuidInvestigation,
  value,
  onChange,
  onApply,
  translate,
}) => {
  const { researchers } = useDepartments();
  const [services, setServices] = useState<
    { id: number; label: string; serviceId: number; serviceLabel: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    getServicesInvestigationService(uuidInvestigation, ServiceType.SURGERY)
      .then((response) => {
        if (cancelled || response.status !== 200) {
          return;
        }
        setServices(
          (response.servicesInvestigation ?? []).map((si) => ({
            id: si.id,
            label: si.description || si.name,
            serviceId: si.service?.id,
            serviceLabel: si.service?.name ?? si.name,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setServices([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuidInvestigation]);

  const procedureOptions = useMemo(() => {
    const byService = new Map<number, string>();
    services.forEach((service) => {
      if (service.serviceId) {
        byService.set(service.serviceId, service.serviceLabel);
      }
    });
    return Array.from(byService.entries()).map(([id, label]) => ({ id, label }));
  }, [services]);

  const statusOptions = useMemo(
    () =>
      SURGERY_FILTER_STATUSES.map((status) => ({
        value: status,
        label: translate(
          `pages.hospital.services.request.status.${STATUS_I18N_KEY[status]}`,
        ),
      })),
    [translate],
  );

  const selectedServices = services.filter((service) =>
    value.serviceInvestigationIds.includes(service.id),
  );
  const selectedProcedures = procedureOptions.filter((option) =>
    value.procedureServiceIds.includes(option.id),
  );
  const selectedSurgeons = researchers.filter((researcher) =>
    value.surgeonResearcherUuids.includes(researcher.uuid),
  );
  const selectedStatuses = statusOptions.filter((option) =>
    value.statuses.includes(option.value),
  );

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        <Translate id="hospital.analytics.surgery.filters.title" />
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={services}
            getOptionLabel={(option) => option.label}
            value={selectedServices}
            onChange={(_, next) =>
              onChange({
                ...value,
                serviceInvestigationIds: next.map((item) => item.id),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.service')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={procedureOptions}
            getOptionLabel={(option) => option.label}
            value={selectedProcedures}
            onChange={(_, next) =>
              onChange({
                ...value,
                procedureServiceIds: next.map((item) => item.id),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.procedure')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={researchers}
            getOptionLabel={(option) => `${option.name} ${option.surnames}`}
            value={selectedSurgeons}
            onChange={(_, next) =>
              onChange({
                ...value,
                surgeonResearcherUuids: next.map((item) => item.uuid),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.surgeon')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={statusOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            value={selectedStatuses}
            onChange={(_, next) =>
              onChange({
                ...value,
                statuses: next.map((item) => item.value),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.status')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {selectedServices.map((service) => (
              <Chip key={`si-${service.id}`} size="small" label={service.label} />
            ))}
            {selectedProcedures.map((procedure) => (
              <Chip key={`proc-${procedure.id}`} size="small" label={procedure.label} />
            ))}
            {selectedSurgeons.map((surgeon) => (
              <Chip
                key={surgeon.uuid}
                size="small"
                label={`${surgeon.name} ${surgeon.surnames}`}
              />
            ))}
            {selectedStatuses.map((status) => (
              <Chip key={status.value} size="small" label={status.label} />
            ))}
            <Button variant="contained" size="small" onClick={onApply}>
              <Translate id="hospital.analytics.surgery.filters.apply" />
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => onChange(EMPTY_SURGERY_ANALYTICS_FILTERS)}
            >
              <Translate id="hospital.analytics.surgery.filters.clear" />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default withLocalize(SurgeryAnalyticsFiltersComponent);
